import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShieldCheck, Truck, RotateCcw, Star } from "lucide-react";
import { fetchProduct, fetchTestimonials } from "../lib/api";
import { useCart } from "../context/CartContext";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Reveal } from "../components/Reveal";
import {
  BeforeAfterTimeline,
  CompetitorComparison,
  BenefitsStats,
} from "../components/ProductInsights";
import { TrustBadges } from "../components/TrustBadges";

const PLAN_KEYS = {
  ONCE: "once",
  THREE: "three",
  SUB: "subscribe",
};

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState({ items: [] });
  const [plan, setPlan] = useState(PLAN_KEYS.SUB);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setPlan(PLAN_KEYS.SUB);
    fetchProduct(id).then(setProduct).catch(() => setProduct(null));
    fetchTestimonials().then(setReviews).catch(() => {});
  }, [id]);

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32 text-white/60">
        Loading…
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7 lg:sticky lg:top-28 self-start">
          <div className="relative aspect-square bg-[#0A0A0A] border border-white/10 overflow-hidden grain">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 80% 70% at 50% 40%, rgba(209,0,0,0.22), transparent 60%)",
              }}
            />
            <img
              src={product.image}
              alt={product.name}
              className="absolute inset-0 m-auto h-[90%] w-auto object-contain"
              data-testid="product-main-image"
            />
            <div className="absolute bottom-6 left-6 font-display text-xs tracking-[0.3em] text-white/60">
              {product.count}
            </div>
          </div>
        </div>

        {/* Buy box */}
        <div className="lg:col-span-5">
          <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
            {product.tagline?.toUpperCase()}
          </div>
          <h1
            className="font-display uppercase leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
            data-testid="product-title"
          >
            {product.name}
          </h1>
          <p className="mt-4 text-white/60 font-body">{product.subtitle}</p>

          <div className="mt-8 flex items-baseline gap-4">
            <div
              className="font-display text-4xl tracking-wider"
              data-testid="product-price"
            >
              ${product.price.toFixed(2)}
            </div>
            <div className="text-white/40 text-sm">USD · {product.count}</div>
          </div>

          <ul className="mt-8 space-y-3">
            {product.highlights?.map((h) => (
              <li key={h} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 bg-[#D10000] shrink-0" />
                <span className="text-white/85 font-body">{h}</span>
              </li>
            ))}
          </ul>

          {/* OFFER STRUCTURING — Subscribe & Save / 3-Bottle / One-Time */}
          <div className="mt-10" data-testid="plan-picker">
            <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mb-3">
              CHOOSE YOUR PLAN
            </div>
            <div className="space-y-3">
              <PlanCard
                active={plan === PLAN_KEYS.SUB}
                onClick={() => setPlan(PLAN_KEYS.SUB)}
                badge="MOST POPULAR"
                title="Subscribe & Save"
                subtitle="Ships every 30 days · Cancel anytime"
                priceLabel={`$${(product.price * 0.85).toFixed(2)}`}
                strikePrice={`$${product.price.toFixed(2)}`}
                savings="Save 15%"
                testid="plan-subscribe"
              />
              {!product.items && (
                <PlanCard
                  active={plan === PLAN_KEYS.THREE}
                  onClick={() => setPlan(PLAN_KEYS.THREE)}
                  badge="BEST VALUE"
                  title="3-Bottle Supply"
                  subtitle="3-month protocol · Best per-bottle price"
                  priceLabel={`$${(product.price * 3 * 0.75).toFixed(2)}`}
                  strikePrice={`$${(product.price * 3).toFixed(2)}`}
                  savings="Save 25%"
                  testid="plan-three"
                />
              )}
              <PlanCard
                active={plan === PLAN_KEYS.ONCE}
                onClick={() => setPlan(PLAN_KEYS.ONCE)}
                title="One-Time Purchase"
                subtitle={`${product.count} · No commitment`}
                priceLabel={`$${product.price.toFixed(2)}`}
                testid="plan-once"
              />
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div className="inline-flex items-center border border-white/15">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease-button"
                className="w-11 h-12 grid place-items-center hover:bg-white/5 disabled:opacity-40"
                aria-label="Decrease quantity"
                disabled={plan === PLAN_KEYS.THREE}
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-display text-lg tracking-wider">
                {plan === PLAN_KEYS.THREE ? 3 : qty}
              </span>
              <button
                onClick={() => setQty((q) => Math.min(20, q + 1))}
                data-testid="qty-increase-button"
                className="w-11 h-12 grid place-items-center hover:bg-white/5 disabled:opacity-40"
                aria-label="Increase quantity"
                disabled={plan === PLAN_KEYS.THREE}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => handleAddToCart(product, plan, qty, addItem)}
              data-testid="add-to-cart-button"
              className="flex-1 bg-[#D10000] hover:bg-[#A30000] transition-colors text-white font-display text-lg tracking-widest uppercase py-3.5"
            >
              {addToCartLabel(product, plan, qty)}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 text-xs text-white/60">
            <div className="flex items-start gap-2">
              <Truck className="h-4 w-4 text-[#D10000] mt-0.5" />
              <span>Free US shipping over $75</span>
            </div>
            <div className="flex items-start gap-2">
              <RotateCcw className="h-4 w-4 text-[#D10000] mt-0.5" />
              <span>30-day money-back guarantee</span>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[#D10000] mt-0.5" />
              <span>Third-party tested</span>
            </div>
          </div>

          <p className="mt-10 text-white/70 leading-relaxed">
            {product.description}
          </p>
        </div>
      </section>

      {/* Trust badges from the actual bottle */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8"
        data-testid="trust-badges-section"
      >
        <div className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8">
          <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mb-5">
            CERTIFIED · COMPLIANT · CLEAN
          </div>
          <TrustBadges />
        </div>
      </section>

      {/* Insights: Before/After timeline → Competitor compare → Stats */}
      <BeforeAfterTimeline />
      <CompetitorComparison />
      <BenefitsStats />

      {/* Tabs */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-white/10"
        data-testid="product-tabs-section"
      >
        <Tabs defaultValue="ingredients" className="w-full">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none p-0 h-auto w-full justify-start gap-8 overflow-x-auto scrollbar-none">
            {[
              { v: "ingredients", l: "Ingredients" },
              { v: "how", l: "How It Works" },
              { v: "benefits", l: "Benefits" },
              { v: "reviews", l: "Reviews" },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}-trigger`}
                className="font-display text-base md:text-lg tracking-widest uppercase text-white/50 data-[state=active]:text-[#D10000] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D10000] pb-4 px-0"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="ingredients" className="pt-10">
            {/* Supplement Facts panel */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 mb-8" data-testid="supplement-facts">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6 border-b border-white/15 pb-4">
                <div>
                  <div className="font-display text-xs tracking-[0.3em] text-[#D10000]">
                    SUPPLEMENT FACTS
                  </div>
                  <div className="font-display text-3xl tracking-tight uppercase mt-1">
                    {product.name}
                  </div>
                </div>
                <div className="text-right text-xs text-white/55 leading-relaxed">
                  {product.serving_size && (
                    <div>Serving size: <span className="text-white">{product.serving_size}</span></div>
                  )}
                  {product.servings_per_container && (
                    <div>Servings per container: <span className="text-white">{product.servings_per_container}</span></div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 md:gap-4 text-[10px] tracking-[0.25em] uppercase text-white/40 mb-3">
                <div className="col-span-7">Ingredient</div>
                <div className="col-span-3 text-right">Per Serving</div>
                <div className="col-span-2 text-right">% DV</div>
              </div>
              <ul className="divide-y divide-white/5">
                {product.ingredients?.map((ing, i) => (
                  <li
                    key={i}
                    className="py-4"
                    data-testid={`ingredient-row-${i}`}
                  >
                    <div className="grid grid-cols-12 gap-2 md:gap-4 items-center">
                      <div className="col-span-7">
                        <div className="font-display tracking-tight uppercase text-base md:text-lg">
                          {ing.name}
                        </div>
                        {ing.purpose && (
                          <div className="text-white/45 text-xs mt-0.5">
                            {ing.purpose}
                          </div>
                        )}
                      </div>
                      <div className="col-span-3 text-right text-white/80 text-sm">
                        {ing.dose || "—"}
                      </div>
                      <div className="col-span-2 text-right text-white/55 text-xs">
                        {ing.dv || "**"}
                      </div>
                    </div>
                    {Array.isArray(ing.benefits) && ing.benefits.length > 0 && (
                      <ul className="mt-3 ml-0 md:ml-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-white/65 leading-relaxed">
                        {ing.benefits.map((b, j) => (
                          <li
                            key={j}
                            className="flex gap-2 items-start"
                            data-testid={`ingredient-benefit-${i}-${j}`}
                          >
                            <span className="text-[#D10000] mt-0.5 select-none">›</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-[11px] text-white/40">
                ** Daily Value (DV) not established.
              </div>
              {product.inactive_ingredients && (
                <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/55">
                  <span className="font-display tracking-[0.25em] uppercase text-white/40 mr-2">
                    Inactive Ingredients:
                  </span>
                  {product.inactive_ingredients}
                </div>
              )}
            </div>

            {/* Suggested use */}
            {product.suggested_use && (
              <div className="bg-[#070707] border border-white/10 p-6 md:p-8" data-testid="suggested-use">
                <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-2">
                  SUGGESTED USE
                </div>
                <p className="font-display text-2xl md:text-3xl tracking-tight uppercase leading-tight">
                  {product.suggested_use}
                </p>
                <p className="mt-4 text-white/55 text-xs leading-relaxed max-w-2xl">
                  Caution: Consult with a physician prior to use if you have any
                  medical conditions. Do not use if pregnant or lactating. Keep
                  out of reach of children. Do not use if safety seal is damaged
                  or missing. Store in a cool, dry place.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="how" className="pt-10">
            <ol className="space-y-6 max-w-3xl">
              {product.how_it_works?.map((step, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-6 items-start">
                    <div className="font-display text-5xl text-[#D10000] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="text-white/85 leading-relaxed pt-2">{step}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </TabsContent>

          <TabsContent value="benefits" className="pt-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
              {product.highlights?.map((h, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="bg-[#0A0A0A] p-8 h-full">
                    <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display text-2xl tracking-tight uppercase">
                      {h}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="pt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(reviews.items || []).slice(0, 4).map((r, i) => (
                <Reveal key={r.id || i} delay={i * 0.05}>
                  <div className="bg-[#0A0A0A] border border-white/10 p-8">
                    <div className="flex gap-0.5 mb-4">
                      {[...Array(r.stars)].map((_, k) => (
                        <Star
                          key={k}
                          className="h-4 w-4 fill-[#D10000] text-[#D10000]"
                        />
                      ))}
                    </div>
                    <p className="text-white/85">"{r.quote}"</p>
                    <div className="mt-6 pt-6 border-t border-white/10 text-sm">
                      <div className="font-display tracking-wider uppercase">
                        {r.name}
                      </div>
                      <div className="text-white/40 text-xs mt-1">
                        {r.location}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Transformation Timeline */}
      {Array.isArray(product.transformation_timeline) && product.transformation_timeline.length > 0 && (
        <section
          className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-white/10"
          data-testid="transformation-timeline"
        >
          <Reveal>
            <div className="mb-12 md:mb-16">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
                THE TRANSFORMATION
              </div>
              <h2
                className="font-display uppercase leading-[0.9] tracking-tight"
                style={{ fontSize: "clamp(2rem, 5vw, 3.75rem)" }}
              >
                Before. <span className="text-stroke">After.</span>
              </h2>
              <p className="mt-4 text-white/60 max-w-xl">
                What 8 weeks on {product.name} looks like — week by week.
                Compounding shifts, not overnight hype.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {product.transformation_timeline.map((stage, i) => {
              const isAfter = stage.phase === "AFTER";
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <article
                    className={`relative bg-[#0A0A0A] p-6 md:p-8 h-full ${
                      isAfter ? "ring-1 ring-[#D10000]" : ""
                    }`}
                    data-testid={`timeline-stage-${i}`}
                  >
                    {isAfter && (
                      <div className="absolute top-3 right-3 font-display text-[10px] tracking-[0.3em] text-[#D10000] bg-[#D10000]/10 px-2 py-1">
                        PEAK
                      </div>
                    )}
                    <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-2">
                      {stage.phase}
                    </div>
                    <div className="text-white/40 text-xs mb-4">
                      {stage.window}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight uppercase leading-none mb-5">
                      {stage.headline}
                    </h3>
                    <ul className="space-y-2 text-sm text-white/70 leading-relaxed">
                      {stage.points.map((p, j) => (
                        <li
                          key={j}
                          className="flex gap-2 items-start"
                          data-testid={`timeline-point-${i}-${j}`}
                        >
                          <span className="text-[#D10000] mt-1 select-none">›</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.4}>
            <p className="mt-8 text-white/40 text-xs max-w-2xl">
              * Individual results vary. Timeline reflects typical reports from
              consistent daily use. Primenax is a dietary supplement, not a
              substitute for medical advice. Not intended to diagnose, treat,
              cure, or prevent any disease.
            </p>
          </Reveal>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <Link
          to="/shop"
          data-testid="back-to-shop-link"
          className="font-display tracking-widest uppercase text-white/60 hover:text-white"
        >
          ← Back to Shop
        </Link>
      </section>
    </div>
  );
};

export default ProductDetail;

/* ---------- helpers ---------- */

function addToCartLabel(product, plan, qty) {
  switch (plan) {
    case PLAN_KEYS.THREE:
      return `Add 3-Pack — $${(product.price * 3 * 0.75).toFixed(2)}`;
    case PLAN_KEYS.SUB:
      return `Subscribe — $${(product.price * 0.85 * qty).toFixed(2)}/mo`;
    default:
      return `Add to Cart — $${(product.price * qty).toFixed(2)}`;
  }
}

function handleAddToCart(product, plan, qty, addItem) {
  if (plan === PLAN_KEYS.THREE) {
    addItem(
      {
        ...product,
        id: `${product.id}-3pack`,
        name: `${product.name} — 3-Bottle Supply`,
        price: +(product.price * 3 * 0.75).toFixed(2),
      },
      1
    );
    return;
  }
  if (plan === PLAN_KEYS.SUB) {
    addItem(
      {
        ...product,
        id: `${product.id}-sub`,
        name: `${product.name} — Subscribe & Save`,
        price: +(product.price * 0.85).toFixed(2),
      },
      qty
    );
    return;
  }
  addItem(product, qty);
}

const PlanCard = ({
  active,
  onClick,
  badge,
  title,
  subtitle,
  priceLabel,
  strikePrice,
  savings,
  testid,
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left flex items-center gap-4 px-5 py-4 border transition-colors relative ${
      active
        ? "border-[#D10000] bg-[#D10000]/10"
        : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"
    }`}
  >
    {badge && (
      <span className="absolute -top-2.5 left-4 bg-[#D10000] text-white font-display text-[10px] tracking-[0.25em] px-2 py-0.5">
        {badge}
      </span>
    )}
    <span
      className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
        active ? "border-[#D10000]" : "border-white/30"
      }`}
    >
      {active && <span className="h-2.5 w-2.5 bg-[#D10000] rounded-full" />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="font-display text-lg tracking-tight uppercase block">
        {title}
      </span>
      <span className="text-white/55 text-xs block mt-0.5">{subtitle}</span>
    </span>
    <span className="text-right shrink-0">
      <span className="font-display text-lg tracking-wider block">
        {priceLabel}
      </span>
      {strikePrice && (
        <span className="text-white/35 text-xs line-through block">
          {strikePrice}
        </span>
      )}
      {savings && (
        <span className="text-[#D10000] text-[10px] tracking-[0.25em] uppercase block mt-0.5">
          {savings}
        </span>
      )}
    </span>
  </button>
);
